import { FC } from 'react';
import { useParams, useRouteMatch } from 'react-router-dom';

import AddIcon from '@mui/icons-material/Add';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Grid from '@mui/material/Grid';
import { GridColDef } from '@mui/x-data-grid';
import Can from 'src/components/Can';
import LinkButton from 'src/components/LinkButton';
import Table from 'src/components/Table';
import TableActions from 'src/components/Table/Actions';
import StatusIcon from 'src/components/Table/StatusIcon';
import useApiQueryPagination from 'src/hooks/useApiQueryPagination';
import { IEditParams } from 'src/interfaces/IEditParams';
import { FunnelStep } from 'src/modules/attendance/@types/models';
import { ATTENDANCE_PERMISSIONS } from 'src/modules/attendance/constants/permissions';
import attendanceApi from 'src/modules/attendance/services/api';
import FunnelStepFilterSchema from 'src/modules/attendance/validators/FunnelStep/filter.schema';

import StepFilters from './StepFilters';

const List: FC = () => {
  const { url } = useRouteMatch();
  const params = useParams<IEditParams>();
  const {
    handlePageChange,
    handlePageSizeChange,
    isLoading,
    data: response,
    refetch,
  } = useApiQueryPagination<FunnelStep>((queryConfig) =>
    attendanceApi.admin.funnels.steps.useFunnelStepsQuery(
      params.id,
      queryConfig,
    ),
  );

  const columns: GridColDef<FunnelStep>[] = [
    { field: 'id', headerName: 'ID' },
    { field: 'name', headerName: 'Nome', flex: 1 },
    { field: 'ordination', headerName: 'Ordem' },
    {
      field: 'status',
      headerName: 'Status',
      headerAlign: 'center',
      align: 'center',
      renderCell(params) {
        return <StatusIcon status={params.row.status} />;
      },
    },
    {
      field: 'actions',
      headerName: '',
      align: 'right',
      renderCell({ row }) {
        return (
          <TableActions
            resourceName="Etapa"
            resourceId={row.id}
            resourceStatus={row.status}
            editLink={`${url}/${row.id}`}
            editPermissions={[ATTENDANCE_PERMISSIONS.FUNNELS.UPDATE]}
            destroyApiEndpoint={(resourceId, config) =>
              attendanceApi.admin.funnels.steps.destroyFunnelStep(
                row.funnel_id,
                resourceId,
                config,
              )
            }
            destroyPermissions={[ATTENDANCE_PERMISSIONS.FUNNELS.DESTROY]}
            restoreApiEndpoint={(resourceId, config) =>
              attendanceApi.admin.funnels.steps.restoreFunnelStep(
                row.funnel_id,
                resourceId,
                config,
              )
            }
            restorePermissions={[ATTENDANCE_PERMISSIONS.FUNNELS.RESTORE]}
            descriptionPrefix={`A Etapa ${row.name}`}
            onFinish={refetch}
          />
        );
      },
    },
  ];

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <Grid container justifyContent="end" alignItems="center">
          <Can
            behavior="disable"
            permissions={[ATTENDANCE_PERMISSIONS.FUNNELS.STORE]}
          >
            <LinkButton
              to={`${url}/novo`}
              startIcon={<AddIcon />}
              color="primary"
              variant="contained"
              size="small"
            >
              Nova
            </LinkButton>
          </Can>
        </Grid>
      </Grid>

      <Grid item xs={12}>
        <Card>
          <CardContent>
            <Table
              autoHeight
              rows={response?.data.data || []}
              pageSize={response?.data.meta.per_page}
              rowCount={response?.data.meta.total}
              columns={columns}
              loading={isLoading}
              pagination
              paginationMode="server"
              onPageChange={handlePageChange}
              onPageSizeChange={handlePageSizeChange}
              canSearch
              showFilters
              filterProps={{
                yupSchema: FunnelStepFilterSchema,
                children: <StepFilters />,
              }}
            />
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );
};

export default List;
